import React, { useState, useMemo, useRef, useEffect } from 'react'
import { styled } from 'linaria/react';
import { css, cx } from 'linaria';
import { theme } from '../../Theme';
import t from '@jetshop/intl';

const htmlStyle = css`

    h1,
    h2,
    h3 {
      font-family: ${theme.fonts.primary};
      font-size: 28px;
      font-style: normal;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 12px;
    }
    p {
      line-height: normal;
    }

  
  
`
const HtmlBlockWrapper = styled('div')`
  max-width: 700px;
  margin: 0 auto;
  padding: 16px;
`

const ContentWrapper = styled('div')`
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
  animation: fadeIn 0.4s ease-in-out;
  overflow: hidden;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const AnimatedContentContainer = styled('div')`
  transition: height 0.4s ease-in-out, opacity 0.3s ease-in-out;
  overflow: hidden;
  position: relative;
`

const GradientOverlay = styled('div')`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(255, 255, 255, 1) 100%);
  transition: opacity 0.3s ease-in-out;
`

const ReadMoreButton = styled('button')`
  /* margin-top: 1rem; */
  padding: 0.5rem 1rem;
  background-color: transparent;
  color: #000;
  border: none;
  cursor: pointer;
  font-family: ${theme.fonts.primary};
  font-size: 12px;
  font-weight: 700;
  /* text-transform: uppercase; */
  text-decoration: underline;
  letter-spacing: 0.04em;
  border-radius: 0;
  transition: background-color 0.2s ease, transform 0.2s ease;
  margin: 0 auto!important;
  display: block!important;
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

// Helper function to strip HTML tags and get text content (SSR-safe)
const stripHtml = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined') {
    // SSR fallback: simple regex-based stripping
    return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const tmp = document.createElement('DIV');
  tmp.innerHTML = String(html);
  return tmp.textContent || tmp.innerText || '';
};

// Helper function to count words in text
const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

// Helper function to truncate HTML at word boundary (SSR-safe)
const truncateHtml = (html, wordLimit) => {
  if (!html || !wordLimit) return html || '';
  
  const htmlString = String(html);
  
  if (typeof window === 'undefined') {
    // SSR fallback: simple text-based truncation
    const text = stripHtml(htmlString);
    if (!text) return htmlString;
    
    const words = text.trim().split(/\s+/);
    if (words.length <= wordLimit) {
      return htmlString;
    }
    // Find approximate position in HTML
    const truncatedText = words.slice(0, wordLimit).join(' ');
    const htmlText = stripHtml(htmlString);
    if (!htmlText || htmlText.length === 0) return htmlString;
    
    const ratio = truncatedText.length / htmlText.length;
    const truncatePos = Math.floor(htmlString.length * ratio);
    // Find a safe truncation point (end of a tag or space)
    let safePos = truncatePos;
    for (let i = truncatePos; i < htmlString.length && i < truncatePos + 50; i++) {
      if (htmlString[i] === '>' || htmlString[i] === ' ') {
        safePos = i + 1;
        break;
      }
    }
    // Ensure ellipsis is added
    const truncated = htmlString.substring(0, safePos);
    // Check if ellipsis is already at the end, if not add it
    return truncated.trim().endsWith('...') ? truncated : truncated.trim() + '...';
  }

  const text = stripHtml(htmlString);
  if (!text) return htmlString;
  
  const words = text.trim().split(/\s+/);
  
  if (words.length <= wordLimit) {
    return htmlString;
  }

  // Create a temporary element to parse HTML
  const tmp = document.createElement('DIV');
  tmp.innerHTML = htmlString;
  
  // Walk through text nodes and truncate
  let wordCount = 0;
  const walker = document.createTreeWalker(
    tmp,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  let node;
  let foundTruncationPoint = false;
  while ((node = walker.nextNode())) {
    if (!node || !node.textContent) continue;
    
    if (foundTruncationPoint) {
      // Clear all remaining text nodes after truncation
      node.textContent = '';
      continue;
    }
    
    const nodeWords = node.textContent.trim().split(/\s+/).filter(w => w.length > 0);
    const nodeWordCount = nodeWords.length;
    
    if (wordCount + nodeWordCount <= wordLimit) {
      wordCount += nodeWordCount;
    } else {
      // We've exceeded the word limit
      const remainingWords = wordLimit - wordCount;
      if (remainingWords > 0) {
        const truncatedText = nodeWords.slice(0, remainingWords).join(' ');
        // Add ellipsis immediately when truncating
        node.textContent = truncatedText.trim() + '...';
      } else {
        // Edge case: no remaining words, but we need to add ellipsis
        // Find the previous text node or add ellipsis to current empty node
        node.textContent = '...';
      }
      foundTruncationPoint = true;
    }
  }

  return tmp.innerHTML;
};

export const HtmlBlock = ({ asSEO, html, readMoreWordLimit}) => {
  // Handle both prop name variations (typo and correct)
  const wordLimit = readMoreWordLimit?.value;
  // Parse the limit, but treat empty string, 0, and NaN as null (no read more)
  const parsedLimit = wordLimit ? parseInt(wordLimit, 10) : null;
  const numericLimit = (parsedLimit !== null && !isNaN(parsedLimit) && parsedLimit > 0) ? parsedLimit : null;
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState('auto');
  const contentRef = useRef(null);
  const collapsedRef = useRef(null);
  const expandedRef = useRef(null);
  
  // Only apply SEO improvements when asSEO.value is explicitly true
  const isSeoContent = asSEO?.value === true || asSEO?.value === 'true';
  
  // Process HTML content for read more functionality
  const processedHtml = useMemo(() => {
    if (!html?.value) return '';
    
    if (numericLimit && !isExpanded) {
      const truncated = truncateHtml(html.value, numericLimit);
      // Ensure ellipsis is present - add it if not already there
      if (truncated && !truncated.includes('...')) {
        return truncated + '<span>...</span>';
      }
      return truncated;
    }
    
    return html.value;
  }, [html?.value, numericLimit, isExpanded]);
  
  const shouldShowReadMore = useMemo(() => {
    if (!html?.value || !numericLimit || numericLimit <= 0) return false;
    const text = stripHtml(html.value);
    return countWords(text) > numericLimit;
  }, [html?.value, numericLimit]);
  
  // Measure heights and set content height for animation
  useEffect(() => {
    if (!shouldShowReadMore || typeof window === 'undefined') {
      setContentHeight('auto');
      return;
    }
    
    const updateHeight = () => {
      if (collapsedRef.current && expandedRef.current && contentRef.current) {
        const collapsedHeight = collapsedRef.current?.scrollHeight ?? 0;
        const expandedHeight = expandedRef.current?.scrollHeight ?? 0;
        
        // Set the target height based on expanded state
        const targetHeight = isExpanded ? expandedHeight : collapsedHeight;
        if (targetHeight > 0) {
          setContentHeight(`${targetHeight}px`);
        }
      }
    };
    
    // Use requestAnimationFrame for smoother updates
    const rafId = requestAnimationFrame(() => {
      setTimeout(updateHeight, 0);
    });
    
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isExpanded, html?.value, numericLimit, shouldShowReadMore]);
  
  return (
    <HtmlBlockWrapper 
      as={isSeoContent ? 'article' : 'div'}
      className={cx('html-block', htmlStyle)}
      {...(isSeoContent && {
        itemScope: true,
        itemType: 'https://schema.org/Article',
        role: 'article',
        'aria-label': 'SEO content'
      })}
    >
      {shouldShowReadMore ? (
        <>
          {/* Hidden elements to measure heights - must match actual content styling exactly */}
          <div
            ref={collapsedRef}
            className={cx('html-block', htmlStyle)}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              height: 'auto',
              width: '100%',
              maxWidth: '700px',
              padding: '16px',
              margin: '0 auto',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                overflow: 'hidden',
              }}
              dangerouslySetInnerHTML={{ __html: truncateHtml(html?.value, numericLimit) || '' }}
            />
          </div>
          <div
            ref={expandedRef}
            className={cx('html-block', htmlStyle)}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              height: 'auto',
              width: '100%',
              maxWidth: '700px',
              padding: '16px',
              margin: '0 auto',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                overflow: 'hidden',
              }}
              dangerouslySetInnerHTML={{ __html: html?.value || '' }}
            />
          </div>
          <AnimatedContentContainer style={{ height: contentHeight }}>
            <ContentWrapper 
              ref={contentRef}
              dangerouslySetInnerHTML={{ __html: processedHtml || '' }} 
            />
            {!isExpanded && (
              <GradientOverlay />
            )}
          </AnimatedContentContainer>
        </>
      ) : (
        <ContentWrapper 
          dangerouslySetInnerHTML={{ __html: processedHtml || '' }} 
        />
      )}
      {shouldShowReadMore && (
        <ReadMoreButton onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded 
            ? t('Read less') : t('Read more')
        
          }
        </ReadMoreButton>
      )}
    </HtmlBlockWrapper>
  )
}
